import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import type { IContainers } from '../../interfaces/config'
import useAppSelector from '../useAppSelector'
import { useAlternativeProducts } from './useAlternativeProducts'

/**
 * True when the PDP container references the alternativeProducts carousel. A
 * deliberately coarse probe: does the serialized container contain the
 * `CarouselAlternativeProducts` preset name or the `alternativeProductsSlice`
 * binding anywhere? (Those tokens realistically only appear when the carousel is
 * configured — via the defaultProps preset or a raw items binding — so a broad
 * substring check is enough and is robust to header/footer/body placement.)
 *
 * The carousel SHIPS DARK, so a storefront that hasn't configured it must NOT fire
 * the SKU-keyed query — it 400s on tenants without the alternativeProducts add-on
 * (the `alternativeProductsAddon` middleware, which sources skuMin1/skuMax1). This
 * restores the coupling the old `runOnLoad` had for free (the trigger lived on the
 * carousel component itself).
 */
export const containerHasAlternativeProducts = (
  container?: IContainers,
): boolean => {
  if (!container) return false
  const serialized = JSON.stringify(container)
  return (
    serialized.includes('CarouselAlternativeProducts') ||
    serialized.includes('alternativeProductsSlice')
  )
}

/**
 * PDP-scoped trigger for the "Opciones similares" (alternativeProducts) carousel.
 *
 * The carousel is SKU-keyed — it must fetch alternatives for the product shown on
 * the product-detail page. It CANNOT be driven by a `runOnLoad` template binding:
 * `{{params.sku}}` is a source shortcut for `state.defaultSlice.params.sku`, but
 * `RouterParams` (mounted at the app root) populates that in an effect that runs
 * *after* a deep carousel's one-shot `runOnLoad` fires — so at fire time the value
 * is empty and the binding is never retried. (The same `{{params.sku}}` resolves
 * fine in the counter's cart actions, because those resolve at click time, once
 * `params` is populated.)
 *
 * So we trigger the fetch here instead, reactively, from the mounted PDP template:
 * read the sku from the route (`useParams`) and (re-)fetch whenever it changes. On
 * an sku change the underlying fetch drops the previous product's items before
 * loading; a missing sku / not-yet-bootstrapped identifiers simply no-op, and the
 * effect re-runs once the identifiers arrive.
 *
 * Because this template renders for EVERY tenant's PDP, the fetch is gated on the
 * carousel being present in `container` — otherwise a dark (unconfigured)
 * storefront would fire the query and 400 (COM-434).
 */
export const useAlternativeProductsPdpTrigger = (container?: IContainers) => {
  const { sku } = useParams()
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const fetchAlternativeProducts = useAlternativeProducts()

  const isConfigured = useMemo(
    () => containerHasAlternativeProducts(container),
    [container],
  )

  useEffect(() => {
    // Gate 1: the carousel must be configured for this storefront's PDP (ships
    // dark). Gate 2: on the mounted PDP the route guarantees `sku`;
    // `storeName`/`sessionId` can be briefly empty before session bootstrap, so
    // this is a "not ready" no-op that the dep array re-runs once they populate.
    if (!isConfigured || !sku || !storeName || !sessionId) return
    fetchAlternativeProducts({
      storefrontName: storeName,
      sessionUid: sessionId,
      skus: [sku],
    })
    // `fetchAlternativeProducts` is re-created every render; fire only when the
    // gate, displayed sku, or the store identifiers change — not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigured, sku, storeName, sessionId])
}
